(() => {
  "use strict";

  const wrapIndex = (index, total) =>
    total > 0 ? ((index % total) + total) % total : 0;

  function relativePosition(index, activeIndex, total) {
    if (total <= 1) return 0;
    let distance = wrapIndex(index - activeIndex, total);
    if (distance > total / 2) distance -= total;
    return distance;
  }

  function positionName(distance) {
    if (distance === 0) return "current";
    if (distance === 1) return "next";
    if (distance === -1) return "previous";
    if (distance === 2) return "next-far";
    if (distance === -2) return "previous-far";
    return "hidden";
  }

  function buildPositionState(activeIndex, total) {
    return Array.from({ length: total }, (_, index) => {
      const current = index === wrapIndex(activeIndex, total);
      return {
        index,
        position: positionName(relativePosition(index, activeIndex, total)),
        current,
        tabbable: current,
      };
    });
  }

  function gestureDirection(deltaX, deltaY, threshold = 48) {
    if (
      Math.abs(deltaX) < threshold ||
      Math.abs(deltaX) <= Math.abs(deltaY) * 1.2
    ) {
      return 0;
    }
    return deltaX < 0 ? 1 : -1;
  }

  function initializeCarousel(root) {
    if (root.dataset.carouselReady === "true") return;
    root.dataset.carouselReady = "true";

    const cards = [...root.querySelectorAll("[data-carousel-card]")];
    const previousButton = root.querySelector("[data-carousel-previous]");
    const nextButton = root.querySelector("[data-carousel-next]");
    const viewport = root.querySelector("[data-carousel-viewport]");
    const liveRegion = root.querySelector("[data-carousel-status]");
    let activeIndex = 0;
    let pointerStart = null;

    if (cards.length === 0) {
      root.classList.add("is-empty");
      return;
    }

    const render = (nextIndex) => {
      activeIndex = wrapIndex(nextIndex, cards.length);
      const states = buildPositionState(activeIndex, cards.length);

      states.forEach((state) => {
        const card = cards[state.index];
        const link = card.querySelector("a");
        card.dataset.position = state.position;
        card.setAttribute("aria-current", state.current ? "true" : "false");
        card.setAttribute(
          "aria-hidden",
          state.position === "hidden" ? "true" : "false",
        );
        if (link) link.tabIndex = state.tabbable ? 0 : -1;
      });

      root.classList.add("is-ready");
      root.classList.toggle("is-single", cards.length <= 1);
      if (liveRegion) {
        liveRegion.textContent = `第 ${activeIndex + 1} 张，共 ${cards.length} 张`;
      }
    };

    previousButton?.addEventListener("click", () => render(activeIndex - 1));
    nextButton?.addEventListener("click", () => render(activeIndex + 1));

    cards.forEach((card, index) => {
      card.addEventListener("click", (event) => {
        if (index === activeIndex || event.target.closest("a")) return;
        render(index);
      });
    });

    root.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        render(activeIndex - 1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        render(activeIndex + 1);
      }
    });

    viewport?.addEventListener("pointerdown", (event) => {
      pointerStart = { x: event.clientX, y: event.clientY };
      root.classList.add("is-dragging");
    });

    viewport?.addEventListener("pointermove", (event) => {
      if (!pointerStart) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      if (Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      root.style.setProperty("--jmpc-drag-x", `${deltaX * 0.35}px`);
    });

    viewport?.addEventListener("pointerup", (event) => {
      if (!pointerStart) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      pointerStart = null;
      root.classList.remove("is-dragging");
      root.style.removeProperty("--jmpc-drag-x");
      render(activeIndex + gestureDirection(deltaX, deltaY));
    });

    viewport?.addEventListener("pointercancel", () => {
      pointerStart = null;
      root.classList.remove("is-dragging");
      root.style.removeProperty("--jmpc-drag-x");
    });

    render(0);
  }

  function initializeAll(scope = document) {
    scope
      .querySelectorAll("[data-stacked-product-carousel]")
      .forEach(initializeCarousel);
  }

  window.JinmangStackedCarouselTest = {
    wrapIndex,
    relativePosition,
    positionName,
    buildPositionState,
    gestureDirection,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });
})();
